import { Fragment, type ReactNode } from "react";
import { Button } from "../components/button";
import { Icon } from "../components/icon";
import { Kbd } from "../components/states";
import { useShell } from "./app-shell";
import { AppLink } from "./link";

export interface Crumb {
  label: string;
  href?: string;
}

export function Breadcrumbs({ items, label = "Breadcrumb" }: { items: readonly Crumb[]; label?: string }) {
  return (
    <nav aria-label={label} className="min-w-0 truncate text-sm text-fg-muted">
      <ol className="flex min-w-0 items-center gap-1.5">
        {items.map((item, index) => {
          const last = index === items.length - 1;
          return (
            <Fragment key={`${item.label}-${String(index)}`}>
              <li className="min-w-0 truncate">
                {item.href && !last ? (
                  <AppLink href={item.href} className="inline-flex min-h-6 min-w-6 max-w-full items-center hover:text-fg pointer-coarse:min-h-11">
                    {item.label}
                  </AppLink>
                ) : (
                  <span className={last ? "font-bold text-fg" : undefined} aria-current={last ? "page" : undefined}>
                    {item.label}
                  </span>
                )}
              </li>
              {last ? null : (
                <li aria-hidden="true" className="flex">
                  <Icon name="chevron-right" className="size-3.5 text-fg-subtle" />
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}

export interface TopbarProps {
  breadcrumbs: ReactNode;
  actions: ReactNode;
  openMenuLabel: string;
}

/** Sticky 56px topbar: mobile menu button, breadcrumbs, right-aligned actions. */
export function Topbar({ breadcrumbs, actions, openMenuLabel }: TopbarProps) {
  const { openSidebar, sidebarOpen, sidebarId } = useShell();
  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-line bg-surface/95 px-4 backdrop-blur lg:px-6">
      <Button
        variant="ghost"
        iconOnly
        aria-label={openMenuLabel}
        aria-expanded={sidebarOpen}
        aria-controls={sidebarId}
        className="shrink-0 lg:hidden pointer-coarse:h-11 pointer-coarse:w-11"
        onClick={openSidebar}
      >
        <Icon name="menu" />
      </Button>
      {breadcrumbs}
      <div className="ml-auto flex items-center gap-1.5">{actions}</div>
    </header>
  );
}

export interface SearchTriggerProps {
  label: string;
  shortcut: string;
  onOpen: () => void;
}

/** "Search or jump to…" field-like button (md+) and icon button (mobile). */
export function SearchTrigger({ label, shortcut, onOpen }: SearchTriggerProps) {
  return (
    <>
      <button
        type="button"
        onClick={onOpen}
        className="hidden h-9 w-64 items-center gap-2 rounded-md border border-line bg-surface-2 px-3 text-sm text-fg-subtle hover:border-line-strong md:flex"
      >
        <Icon name="search" />
        <span>{label}</span>
        <Kbd className="ml-auto">{shortcut}</Kbd>
      </button>
      <Button variant="ghost" iconOnly aria-label={label} className="md:hidden pointer-coarse:h-11 pointer-coarse:w-11" onClick={onOpen}>
        <Icon name="search" />
      </Button>
    </>
  );
}
